import type { FoundationDatumVerticalAnchor } from "building-types/datums/datumsTypesEnum";
import type { Doc } from "building-types/doc";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import { getFloorAssemblyEntity } from "building-types/floors/floorAssemblyEntityTypes";
import type { FloorAssemblyEntity } from "building-types/floors/floorAssemblyEntityTypes";
import type {
  FloorAssembly,
  FloorAssemblyHashParameters,
  FloorAssemblyOverride,
  HorizontalFloorAssemblyBase,
  HorizontalFloorAssemblyFreeForm,
  HorizontalFloorAssemblyLegacy,
  HorizontalFloorAssemblyRoomDerived,
  SolidFloorAssembly,
  SolidFloorAssemblyOverride,
  SolidFramingFloorAssembly,
  SolidFramingFloorAssemblyOverride,
  SolidSlabFloorAssembly,
  SolidSlabFloorAssemblyOverride,
  VoidFloorAssembly,
  VoidFloorAssemblyOverride,
} from "building-types/floors/floorAssemblyTypes";
import { FloorAssemblyType, HorizontalFloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import type { FloorComponentEntity } from "building-types/floors/floorComponentEntityTypes";
import type { SpanDirection } from "building-types/floors/floorComponentsTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { FloorSplitData } from "building-types/floors/floorSplitDataTypes";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { SlabFloorAssemblyEntity } from "building-types/floors/slabFloorAssemblyTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { Level } from "building-types/levels/levelTypes";
import type { RoomAttributes } from "building-types/rooms/staticRoomAttributes";
import type { FloorSettings } from "building-types/settings/floorSettings";
import type { Table } from "common/data/table";
import type { LevelInches, WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { levelInchesNew, worldInchesNew } from "common/utils/nominalNumbers";
import { defaultFrameFloorAssemblyId } from "doc/defaults/defaultFloorAssembly";
import { docGetRoomIdsForLevel } from "doc/elements/docGetBlocks";
import { default2x12Id } from "doc/elements/framing/defaultLumberComponents";
import { roomClassBaseOffset } from "doc/rooms/roomAttributesTools";
import { getFramingProfileDescriptor } from "doc/utils/lumberTools";
import { defaultFloorSheathingComponentId } from "../../defaults/assemblyComponentDefaults";
import { defaultSlabComponentId } from "../../defaults/assemblyComponentDefaults";
import { normalizeFloorSplitData } from "./floorSplitDataTools";

/**
 * Scan doc.floors to find which floor owns a given bay by its ID.
 * O(N) in the number of floors — typically very small (<20).
 */
export function getFloorIdForBay(floors: Table<FloorAssemblyOverride> | undefined, bayId: string): string | undefined {
  if (!floors) {
    return undefined;
  }
  for (const floorId in floors) {
    const splitData = getFloorSplitData(floors[floorId]);
    if (splitData?.bays?.[bayId]) {
      return floorId;
    }
  }
  return undefined;
}

/**
 * Scan doc.floors to find which floor owns a given split line by its ID.
 * O(N) in the number of floors — typically very small (<20).
 */
export function getFloorIdForSplitLine(
  floors: Table<FloorAssemblyOverride> | undefined,
  splitLineId: string,
): string | undefined {
  if (!floors) {
    return undefined;
  }
  for (const floorId in floors) {
    const splitData = getFloorSplitData(floors[floorId]);
    if (splitData?.splitLines?.[splitLineId]) {
      return floorId;
    }
  }
  return undefined;
}

const DEFAULT_FRAMING_COMPONENT_HEIGHT = 1.5;

export function getFloorComponentThickness(floorComponent: FloorComponentEntity): number {
  if (floorComponent.entityType === "FRAMING_COMPONENT") {
    return getFramingProfileDescriptor(floorComponent).width;
  } else {
    return floorComponent.thickness;
  }
}

export function frameFloorAssemblyExistingOrDefault(
  assemblies: Table<FrameFloorAssemblyEntity>,
  assemblyId: string,
): string {
  return assemblies[assemblyId] ? assemblyId : defaultFrameFloorAssemblyId;
}

export function sheathingComponentExistingOrDefault(
  components: Table<SheathingComponentEntity>,
  componentId: string,
): string {
  return components[componentId] ? componentId : defaultFloorSheathingComponentId;
}

export function slabComponentExistingOrDefault(components: Table<SlabComponentEntity>, componentId: string): string {
  return components[componentId] ? componentId : defaultSlabComponentId;
}

export function voidFloorAssemblyNew(): VoidFloorAssembly {
  return {
    assemblyType: FloorAssemblyType.VOID,
  };
}

export function isVoidFloorAssembly(assembly: FloorAssembly): assembly is VoidFloorAssembly {
  return assembly?.assemblyType === FloorAssemblyType.VOID;
}

export function isVoidFloorAssemblyOverride(assembly: FloorAssemblyOverride): assembly is VoidFloorAssemblyOverride {
  return assembly?.assemblyType === FloorAssemblyType.VOID;
}

export function isSolidFloorAssembly(assembly: FloorAssembly): assembly is SolidFloorAssembly {
  return assembly?.assemblyType === FloorAssemblyType.SOLID;
}

export function isSolidFloorAssemblyOverride(assembly: FloorAssemblyOverride): assembly is SolidFloorAssemblyOverride {
  return assembly?.assemblyType === FloorAssemblyType.SOLID;
}

export function isHorizontalFloorAssemblyLegacy(floor: {
  horizontalAssemblyType?: HorizontalFloorAssemblyType;
}): floor is HorizontalFloorAssemblyLegacy {
  return floor.horizontalAssemblyType === HorizontalFloorAssemblyType.LEGACY;
}

export function isHorizontalFloorAssemblyFreeForm(floor: {
  horizontalAssemblyType?: HorizontalFloorAssemblyType;
}): floor is HorizontalFloorAssemblyFreeForm {
  return floor.horizontalAssemblyType === HorizontalFloorAssemblyType.FREE_FORM;
}

export function isHorizontalFloorAssemblyRoomDerived(floor: {
  horizontalAssemblyType?: HorizontalFloorAssemblyType;
}): floor is HorizontalFloorAssemblyRoomDerived {
  return floor.horizontalAssemblyType === HorizontalFloorAssemblyType.ROOM_DERIVED;
}

export function floorHasBoundarySource(floor: HorizontalFloorAssemblyRoomDerived, perimeterDrawn: boolean): boolean {
  return perimeterDrawn || (floor.roomIds?.length ?? 0) > 0 || !!floor.allRoomsOnLevel;
}

export function allRoomsModeRefProps(
  levelId: string | undefined,
): Pick<HorizontalFloorAssemblyRoomDerived, "allRoomsOnLevel" | "excludedRoomIds" | "roomIds" | "levelId"> {
  return { allRoomsOnLevel: true, excludedRoomIds: [], roomIds: undefined, levelId };
}

/**
 * Resolves room-reference props for a new or updated room-derived floor. Defaults to
 * all rooms on the floor's level when the caller does not specify manual roomIds or
 * an explicit allRoomsOnLevel value.
 */
export function defaultRoomDerivedRefProps(
  override: Partial<HorizontalFloorAssemblyRoomDerived>,
): Pick<HorizontalFloorAssemblyRoomDerived, "allRoomsOnLevel" | "excludedRoomIds" | "roomIds" | "levelId"> {
  if (override.allRoomsOnLevel !== undefined) {
    return {
      levelId: override.levelId,
      allRoomsOnLevel: override.allRoomsOnLevel,
      excludedRoomIds: override.excludedRoomIds ?? [],
      roomIds: override.roomIds,
    };
  }
  if (override.roomIds !== undefined) {
    return {
      levelId: override.levelId,
      roomIds: override.roomIds,
    };
  }
  return allRoomsModeRefProps(override.levelId);
}

export function roomDerivedRefPropsForHostLevelChange(
  floor: HorizontalFloorAssemblyRoomDerived,
  nextLevelId: string | undefined,
): Partial<HorizontalFloorAssemblyRoomDerived> | undefined {
  if (floor.levelId === nextLevelId) {
    return undefined;
  }

  if (floor.allRoomsOnLevel) {
    const existingSplitData = floor.splitData;
    const normalizedSplitData =
      existingSplitData !== undefined
        ? normalizeFloorSplitData({ splitLines: existingSplitData.splitLines, bays: {} })
        : undefined;
    return {
      ...allRoomsModeRefProps(nextLevelId),
      ...(normalizedSplitData !== undefined ? { splitData: normalizedSplitData } : {}),
    };
  }

  return {
    levelId: nextLevelId,
    allRoomsOnLevel: undefined,
    excludedRoomIds: undefined,
    roomIds: [],
    splitData: undefined,
  };
}

export function subtractExcluded(
  roomIds: readonly ForeignEntityId<"ROOM">[],
  excludedRoomIds: readonly ForeignEntityId<"ROOM">[] | undefined,
): ForeignEntityId<"ROOM">[] {
  if (!excludedRoomIds || excludedRoomIds.length === 0) {
    return [...roomIds];
  }
  const excluded = new Set(excludedRoomIds);
  return roomIds.filter((roomId) => !excluded.has(roomId));
}

export function resolveRoomDerivedRoomIds(
  doc: Pick<Doc, "rooms" | "blocks" | "entityRefs">,
  floor: HorizontalFloorAssemblyRoomDerived,
): ForeignEntityId<"ROOM">[] {
  if (floor.allRoomsOnLevel) {
    const roomIdsAtLevel = docGetRoomIdsForLevel(doc, floor.levelId) as ForeignEntityId<"ROOM">[];
    return subtractExcluded(roomIdsAtLevel, floor.excludedRoomIds);
  }
  return [...(floor.roomIds ?? [])];
}

export function removeRoomRefFromFloor(
  floor: HorizontalFloorAssemblyRoomDerived,
  roomId: ForeignEntityId<"ROOM">,
): Partial<Pick<HorizontalFloorAssemblyRoomDerived, "roomIds" | "excludedRoomIds">> {
  const roomIds = floor.roomIds?.includes(roomId) ? floor.roomIds.filter((id) => id !== roomId) : undefined;
  const excludedRoomIds = floor.excludedRoomIds?.includes(roomId)
    ? floor.excludedRoomIds.filter((id) => id !== roomId)
    : undefined;
  return {
    ...(roomIds !== undefined ? { roomIds } : {}),
    ...(excludedRoomIds !== undefined ? { excludedRoomIds } : {}),
  };
}

/**
 * Returns split data as stored on the floor entity, without applying doc invariants.
 * Prefer {@link getFloorSplitData} for reads; use this only when mutating or reconciling stored data.
 */
export function getStoredFloorSplitData(floor: FloorAssemblyOverride): FloorSplitData | undefined {
  return (floor as HorizontalFloorAssemblyBase<HorizontalFloorAssemblyType>).splitData;
}

/**
 * Returns doc split data for a floor. Bays are only present when at least one split line exists.
 */
export function getFloorSplitData(floor: FloorAssemblyOverride): FloorSplitData | undefined {
  return normalizeFloorSplitData(getStoredFloorSplitData(floor));
}

/**
 * Returns true for floor assembly types that store openings in the doc.
 * Only free-form floors have doc-stored openings; room-derived openings are
 * generated at build time from rooms with remove floor structure tag on.
 */
export function isHorizontalFloorAssemblyWithOpenings(floor: {
  horizontalAssemblyType?: HorizontalFloorAssemblyType;
}): floor is HorizontalFloorAssemblyFreeForm {
  return floor.horizontalAssemblyType === HorizontalFloorAssemblyType.FREE_FORM;
}

export function isSolidFramingFloorAssembly(assembly: SolidFloorAssembly): assembly is SolidFramingFloorAssembly {
  return assembly?.assemblyType === FloorAssemblyType.SOLID && assembly.structuralType === StructuralFloorType.FRAMING;
}

export function isSolidFramingFloorAssemblyOverride(
  assembly: SolidFloorAssemblyOverride,
): assembly is SolidFramingFloorAssemblyOverride {
  return assembly?.assemblyType === FloorAssemblyType.SOLID && assembly.structuralType === StructuralFloorType.FRAMING;
}

export function isSolidSlabFloorAssembly(assembly: SolidFloorAssembly): assembly is SolidSlabFloorAssembly {
  return assembly?.assemblyType === FloorAssemblyType.SOLID && assembly.structuralType === StructuralFloorType.SLAB;
}

export function solidFramingFloorAssemblyNew({
  assemblyId,
  floorJoist,
  rimBoard,
  subFloor,
  topDatumOffset,
  spanDirection,
}: {
  assemblyId: ForeignEntityId<"FRAME_FLOOR_ASSEMBLY">;
  floorJoist: ForeignEntityId<"FRAMING_COMPONENT">;
  rimBoard: ForeignEntityId<"FRAMING_COMPONENT">;
  subFloor: ForeignEntityId<"SHEATHING_COMPONENT">;
  topDatum?: FoundationDatumVerticalAnchor;
  topDatumOffset?: number;
  spanDirection?: SpanDirection;
}): SolidFloorAssembly {
  return {
    assemblyType: FloorAssemblyType.SOLID,
    assemblyId,
    floorJoist,
    rimBoard,
    subFloor,
    topDatumOffset,
    spanDirection,
    structuralType: StructuralFloorType.FRAMING,
  };
}

export function solidSlabFloorAssemblyNew({
  assemblyId,
  slab,
  subFloor,
  topDatumOffset,
}: {
  assemblyId: ForeignEntityId<"SLAB_FLOOR_ASSEMBLY">;
  slab: ForeignEntityId<"SLAB_COMPONENT">;
  subFloor: ForeignEntityId<"SHEATHING_COMPONENT">;
  topDatum?: FoundationDatumVerticalAnchor;
  topDatumOffset?: number;
}): SolidFloorAssembly {
  return {
    assemblyType: FloorAssemblyType.SOLID,
    assemblyId,
    slab,
    subFloor,
    topDatumOffset,
    structuralType: StructuralFloorType.SLAB,
  };
}

function makeSolidFloorAssembly(
  assemblyId: string,
  floorAssemblyEntity: FloorAssemblyEntity,
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorAssemblyBaseOffset?: number,
  floorAssemblyOverride?: SolidFloorAssemblyOverride,
) {
  let topDatumOffset = floorAssemblyBaseOffset ?? 0;

  if (floorAssemblyOverride?.topDatumOffset !== undefined) {
    topDatumOffset = floorAssemblyBaseOffset ?? 0;
    topDatumOffset += floorAssemblyOverride.topDatumOffset;
  }

  if (floorAssemblyEntity.entityType === "FRAME_FLOOR_ASSEMBLY") {
    const floorJoist = framingComponents[floorAssemblyEntity.joistComponentId]
      ? floorAssemblyEntity.joistComponentId
      : default2x12Id;
    const rimBoard = framingComponents[floorAssemblyEntity.rimComponentId]
      ? floorAssemblyEntity.rimComponentId
      : floorJoist;
    const subFloor = sheathingComponents[floorAssemblyEntity.subFloorComponentId]
      ? floorAssemblyEntity.subFloorComponentId
      : defaultFloorSheathingComponentId;
    if (floorAssemblyOverride?.structuralType === StructuralFloorType.FRAMING) {
      assertIsNotUndefined(subFloor);
      return solidFramingFloorAssemblyNew({
        assemblyId,
        floorJoist,
        rimBoard,
        subFloor,
        topDatumOffset,
        spanDirection: floorAssemblyOverride.spanDirection,
      });
    } else {
      assertIsNotUndefined(subFloor);
      return solidFramingFloorAssemblyNew({
        assemblyId,
        floorJoist,
        rimBoard,
        subFloor,
        topDatumOffset,
      });
    }
  } else {
    const slab = slabComponents[floorAssemblyEntity.slabComponentId]
      ? floorAssemblyEntity.slabComponentId
      : defaultSlabComponentId;
    const subFloor = sheathingComponents[floorAssemblyEntity.subFloorComponentId]
      ? floorAssemblyEntity.subFloorComponentId
      : defaultFloorSheathingComponentId;
    assertIsNotUndefined(subFloor);
    return solidSlabFloorAssemblyNew({
      assemblyId,
      slab,
      subFloor,
      topDatumOffset,
    });
  }
}

const FloorAssemblyHashParameterOrder = [
  "assemblyType",
  "roomClass",
  "roomEnclosure",
  "openToBelow",
  "topDatumOffset",
  "spanDirection",
  "assemblyId",
] as const satisfies (keyof FloorAssemblyHashParameters)[];

export type FloorAssemblyHashParamSet = Set<keyof FloorAssemblyHashParameters>;

export const defaultFloorAssemblyHashParamSet: FloorAssemblyHashParamSet = new Set([
  "assemblyType",
  "roomClass",
  "topDatumOffset",
  "spanDirection",
  "assemblyId",
]);

function computeFloorAssemblyHash(
  params: FloorAssemblyHashParameters,
  paramSet: FloorAssemblyHashParamSet = defaultFloorAssemblyHashParamSet,
): string {
  let hash = "";
  for (let i = 0; i < FloorAssemblyHashParameterOrder.length; i++) {
    const field = FloorAssemblyHashParameterOrder[i];
    if (paramSet.has(field)) {
      hash += `${params[field]}`;
    }

    if (i < FloorAssemblyHashParameterOrder.length - 1) {
      hash += ".";
    }
  }

  return hash;
}

export function floorAssemblyHash(
  roomAttributes: RoomAttributes,
  assembly: FloorAssembly,
  hashParamsToInclude?: FloorAssemblyHashParamSet,
): { hash: string; hashParams: Partial<FloorAssemblyHashParameters> } {
  if (assembly.assemblyType === FloorAssemblyType.VOID) {
    return { hash: "VOID", hashParams: {} };
  }

  const { assemblyId, topDatumOffset, assemblyType } = assembly;

  let spanDirection: SpanDirection | "INHERIT" = "INHERIT";

  if (assembly.structuralType === StructuralFloorType.FRAMING) {
    spanDirection = assembly.spanDirection ?? "INHERIT";
  }

  const hashParams: FloorAssemblyHashParameters = {
    assemblyType,
    roomClass: roomAttributes.roomClass,
    roomEnclosure: roomAttributes.roomEnclosure,
    openToBelow: !!roomAttributes.openToBelow,
    topDatumOffset: topDatumOffset ?? 0,
    spanDirection,
    assemblyId,
  };

  const hash = computeFloorAssemblyHash(hashParams, hashParamsToInclude);

  return { hash, hashParams };
}

export function makeRoomFloorAssembly({
  level,
  roomAttributes,
  frameFloorAssemblies,
  slabFloorAssemblies,
  framingComponents,
  slabComponents,
  sheathingComponents,
  floorAssemblyOverride,
}: {
  level: Level;
  roomAttributes: RoomAttributes;
  frameFloorAssemblies: Table<FrameFloorAssemblyEntity>;
  slabFloorAssemblies: Table<SlabFloorAssemblyEntity>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  floorAssemblyOverride?: FloorAssemblyOverride;
}): FloorAssembly | undefined {
  if (floorAssemblyOverride?.assemblyType === FloorAssemblyType.VOID) {
    return voidFloorAssemblyNew();
  }

  let floorAssemblyId = floorAssemblyOverride?.assemblyId ?? level.floorAssemblyId;
  let floorAssemblyEntity =
    getFloorAssemblyEntity(floorAssemblyId, frameFloorAssemblies, slabFloorAssemblies) ??
    getFloorAssemblyEntity(level.floorAssemblyId, frameFloorAssemblies, slabFloorAssemblies);

  if (!floorAssemblyEntity) {
    floorAssemblyId = defaultFrameFloorAssemblyId;
    floorAssemblyEntity = getFloorAssemblyEntity(floorAssemblyId, frameFloorAssemblies, slabFloorAssemblies);
  }
  assertIsNotUndefined(floorAssemblyEntity);

  const floorAssemblyBaseOffset = roomClassBaseOffset(roomAttributes.roomClass, level);

  return makeSolidFloorAssembly(
    floorAssemblyId,
    floorAssemblyEntity,
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorAssemblyBaseOffset,
    floorAssemblyOverride,
  );
}

export function makeLevelFloorAssembly({
  floorAssemblyId,
  floorSettings,
  frameFloorAssemblies,
  slabFloorAssemblies,
  framingComponents,
  slabComponents,
  sheathingComponents,
}: {
  floorAssemblyId?: string;
  floorSettings: FloorSettings;
  frameFloorAssemblies: Table<FrameFloorAssemblyEntity>;
  slabFloorAssemblies: Table<SlabFloorAssemblyEntity>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
}): SolidFloorAssembly | undefined {
  if (!floorAssemblyId || !getFloorAssemblyEntity(floorAssemblyId, frameFloorAssemblies, slabFloorAssemblies)) {
    floorAssemblyId = floorSettings.floorAssembly;
  }

  if (!floorAssemblyId || !getFloorAssemblyEntity(floorAssemblyId, frameFloorAssemblies, slabFloorAssemblies)) {
    floorAssemblyId = defaultFrameFloorAssemblyId;
  }

  const floorAssemblyEntity = getFloorAssemblyEntity(floorAssemblyId, frameFloorAssemblies, slabFloorAssemblies);
  assertIsNotUndefined(floorAssemblyEntity);

  return makeSolidFloorAssembly(
    floorAssemblyId,
    floorAssemblyEntity,
    framingComponents,
    slabComponents,
    sheathingComponents,
  );
}

function retrieveStructuralAndSubFloorComponents(
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorAssembly: FloorAssembly,
): {
  rimBoard?: FramingComponentEntity;
  floorJoist?: FramingComponentEntity;
  slab?: SlabComponentEntity;
  subFloor?: SheathingComponentEntity;
} {
  if (floorAssembly.assemblyType === FloorAssemblyType.VOID) {
    return {};
  }
  if (floorAssembly.structuralType === StructuralFloorType.FRAMING) {
    return {
      rimBoard: framingComponents[floorAssembly.rimBoard],
      floorJoist: framingComponents[floorAssembly.floorJoist],
      subFloor: sheathingComponents[floorAssembly.subFloor],
    };
  } else {
    return {
      slab: slabComponents[floorAssembly.slab],
      subFloor: sheathingComponents[floorAssembly.subFloor],
    };
  }
}

export function floorAssemblyTop(floorAssembly: FloorAssembly, level: Level): WorldInches {
  return worldInchesNew(level.floorElevation + floorAssemblyBaseOffset(floorAssembly));
}

export function floorAssemblyBaseOffset(floorAssembly: FloorAssembly): LevelInches {
  return levelInchesNew(floorAssembly.assemblyType === FloorAssemblyType.VOID ? 0 : floorAssembly.topDatumOffset ?? 0);
}

export function floorAssemblyFloorPlateThickness(
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorAssembly: FloorAssembly,
) {
  const structuralThickness = floorAssemblyRimBoardThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorAssembly,
  );
  const subfloorThickness = floorAssemblySubFloorThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorAssembly,
  );
  return structuralThickness + subfloorThickness;
}

/*Note this returns the rimboard thickness as that is what we want to use for the floor plate thickness.*/
export function floorAssemblyFloorPlateThicknessesByComponents(
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorAssembly: FloorAssembly,
) {
  return {
    structuralThickness: floorAssemblyRimBoardThickness(
      framingComponents,
      slabComponents,
      sheathingComponents,
      floorAssembly,
    ),
    subfloorThickness: floorAssemblySubFloorThickness(
      framingComponents,
      slabComponents,
      sheathingComponents,
      floorAssembly,
    ),
  };
}

export function floorAssemblySubFloorThickness(
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorAssembly: FloorAssembly,
) {
  const { subFloor } = retrieveStructuralAndSubFloorComponents(
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorAssembly,
  );
  return subFloor ? getFloorComponentThickness(subFloor) : 0;
}

export function floorAssemblyJoistComponentHeight(
  framingComponents: Table<FramingComponentEntity>,
  floorAssembly: SolidFramingFloorAssembly,
): number {
  const component = framingComponents[floorAssembly.floorJoist];
  if (!component) {
    return DEFAULT_FRAMING_COMPONENT_HEIGHT;
  }
  return getFramingProfileDescriptor(component).height;
}

export function floorAssemblyRimBoardThickness(
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorAssembly: FloorAssembly,
) {
  const { rimBoard } = retrieveStructuralAndSubFloorComponents(
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorAssembly,
  );
  return rimBoard ? getFloorComponentThickness(rimBoard) : 0;
}

export function voidFloorAssemblyOverrideNew(
  parentId: DocEntityParentId<"ROOM">,
  id?: string,
  name?: string,
): VoidFloorAssemblyOverride {
  return {
    ...docEntityBaseNew("FLOOR", id),
    assemblyType: FloorAssemblyType.VOID,
    name: name ?? "",
    parentId,
    horizontalAssemblyType: HorizontalFloorAssemblyType.LEGACY,
    labels: {},
  };
}

export function solidSlabFloorAssemblyOverrideNew(
  parentId: DocEntityParentId<"ROOM" | "FLOOR_SYSTEM">,
  assemblyId?: string,
  id?: string,
  name?: string,
): SolidSlabFloorAssemblyOverride {
  return {
    ...docEntityBaseNew("FLOOR", id),
    assemblyType: FloorAssemblyType.SOLID,
    structuralType: StructuralFloorType.SLAB,
    assemblyId: assemblyId,
    name: name ?? "",
    parentId,
    horizontalAssemblyType: HorizontalFloorAssemblyType.LEGACY,
    labels: {},
  };
}

export function solidFramingFloorAssemblyOverrideNew(
  parentId: DocEntityParentId<"ROOM" | "FLOOR_SYSTEM">,
  assemblyId?: string,
  id?: string,
  name?: string,
): SolidFramingFloorAssemblyOverride {
  return {
    ...docEntityBaseNew("FLOOR", id),
    assemblyType: FloorAssemblyType.SOLID,
    structuralType: StructuralFloorType.FRAMING,
    assemblyId: assemblyId,
    name: name ?? "",
    parentId,
    horizontalAssemblyType: HorizontalFloorAssemblyType.LEGACY,
    labels: {},
  };
}
